import type { RowSelectionState } from '@tanstack/react-table';

/** Узел дерева для рекурсивного выбора: строка TanStack подходит как есть. */
export interface ITreeSelectionNode {
    readonly id: string;
    readonly subRows: readonly ITreeSelectionNode[];
    getCanSelect(): boolean;
    getCanExpand(): boolean;
}

/**
 * Рекурсивный выбор в дереве (`recursiveSelection`) по правилам TreeList DX:
 * - отметка и снятие узла расходятся на всех его загруженных потомков, в том числе скрытых фильтром;
 * - родитель с загруженными детьми отмечен, только если отмечены все выбираемые узлы его ветки, частично
 *   отмеченный в выбор не входит (чекбокс — indeterminate); узел без выбираемых потомков — сам по себе;
 * - узел, отмеченный до прихода детей (ленивая ветка, строка ещё не загружена), передаёт отметку детям,
 *   когда они появятся. Такие узлы копятся в `pendingBranches` — функции ниже его пополняют и чистят.
 * Ключи строк, которых нет в модели (другие страницы, незагруженные ветки), не трогаются.
 */

const isOn = (selection: RowSelectionState, id: string) => selection[id] === true;

const sameSelection = (a: RowSelectionState, b: RowSelectionState) => {
    const keys = Object.keys(a).filter((id) => isOn(a, id));
    return keys.length === Object.keys(b).filter((id) => isOn(b, id)).length && keys.every((id) => isOn(b, id));
};

const indexNodes = <N extends ITreeSelectionNode>(roots: readonly N[]) => {
    const byId = new Map<string, ITreeSelectionNode>();
    const walk = (node: ITreeSelectionNode) => {
        byId.set(node.id, node);
        node.subRows.forEach(walk);
    };
    roots.forEach(walk);
    return byId;
};

/** Отметить или снять всю ветку узла. Невыбираемые узлы ключа не получают, но каскад идёт сквозь них. */
const cascade = (node: ITreeSelectionNode, value: boolean, selection: RowSelectionState) =>
    node.subRows.forEach((child) => {
        if (!value) delete selection[child.id];
        else if (child.getCanSelect()) selection[child.id] = true;
        cascade(child, value, selection);
    });

type BranchState = 'all' | 'some' | 'none';

/** Родители выводятся из детей снизу вверх; `null` — в ветке нечего выбирать. */
const deriveParents = (roots: readonly ITreeSelectionNode[], selection: RowSelectionState) => {
    const walk = (node: ITreeSelectionNode): BranchState | null => {
        let counted = false;
        let all = true;
        let some = false;
        node.subRows.forEach((child) => {
            const state = walk(child);
            if (state === null) return;
            counted = true;
            if (state !== 'all') all = false;
            if (state !== 'none') some = true;
        });
        if (!counted) return node.getCanSelect() ? (isOn(selection, node.id) ? 'all' : 'none') : null;
        const state: BranchState = all ? 'all' : some ? 'some' : 'none';
        if (node.getCanSelect()) {
            if (state === 'all') selection[node.id] = true;
            else delete selection[node.id];
        }
        return state;
    };
    roots.forEach(walk);
};

const prunePending = (pendingBranches: Set<string>, selection: RowSelectionState) =>
    pendingBranches.forEach((id) => {
        if (!isOn(selection, id)) pendingBranches.delete(id);
    });

/**
 * Выбор после изменения `prev → next`: добавленные ключи отмечают свои ветки, родители выводятся из детей.
 * `cascadeRemoved` — снятый ключ снимает и ветку: так ведёт себя клик по чекбоксу; список ключей из кода
 * (`selectRows`) задаёт выбор явно, и снятый из него родитель отмеченных детей не снимает.
 * Возвращает `next` как есть, если нормализация ничего не поменяла.
 */
export function applyTreeSelectionChange<N extends ITreeSelectionNode>(
    roots: readonly N[],
    prev: RowSelectionState,
    next: RowSelectionState,
    pendingBranches: Set<string>,
    cascadeRemoved: boolean,
): RowSelectionState {
    const byId = indexNodes(roots);
    const added = Object.keys(next).filter((id) => isOn(next, id) && !isOn(prev, id));
    const removed = Object.keys(prev).filter((id) => isOn(prev, id) && !isOn(next, id));
    const result: RowSelectionState = { ...next };
    removed.forEach((id) => {
        pendingBranches.delete(id);
        const node = byId.get(id);
        if (cascadeRemoved && node) cascade(node, false, result);
    });
    added.forEach((id) => {
        const node = byId.get(id);
        if (!node) {
            pendingBranches.add(id);
            return;
        }
        if (node.subRows.length) cascade(node, true, result);
        else if (node.getCanExpand()) pendingBranches.add(id);
    });
    deriveParents(roots, result);
    prunePending(pendingBranches, result);
    return sameSelection(result, next) ? next : result;
}

/**
 * Модель строк сменилась: дети узлов из `pendingBranches`, пришедшие только что, получают их отметку,
 * родители выводятся заново. Возвращает `selection` как есть, если ничего не поменялось.
 */
export function syncTreeSelection<N extends ITreeSelectionNode>(
    roots: readonly N[],
    selection: RowSelectionState,
    pendingBranches: Set<string>,
): RowSelectionState {
    const result: RowSelectionState = { ...selection };
    const walk = (node: ITreeSelectionNode) => {
        if (pendingBranches.has(node.id)) {
            if (!isOn(result, node.id)) pendingBranches.delete(node.id);
            else if (node.subRows.length) {
                cascade(node, true, result);
                pendingBranches.delete(node.id);
            } else if (!node.getCanExpand()) pendingBranches.delete(node.id);
        }
        node.subRows.forEach(walk);
    };
    roots.forEach(walk);
    deriveParents(roots, result);
    prunePending(pendingBranches, result);
    return sameSelection(result, selection) ? selection : result;
}
